<template>
  <div class="page-container">
    <div class="page-header">
      <h2>店铺租金账单</h2>
    </div>

    <div class="filter-bar">
      <el-select v-model="shopId" placeholder="选择店铺" size="small" style="width: 200px;" @change="handleShopChange">
        <el-option v-for="shop in shopList" :key="shop.id" :label="shop.name" :value="shop.id" />
      </el-select>
      
      <el-select v-model="exportType" placeholder="导出类型" size="small" style="width: 120px; margin-left: 10px;">
        <el-option label="按年" value="year" />
        <el-option label="按月" value="month" />
        <el-option label="自定义" value="custom" />
      </el-select>

      <template v-if="exportType === 'year'">
        <el-select v-model="year" placeholder="选择年份" size="small" style="width: 120px; margin-left: 10px;">
          <el-option v-for="y in years" :key="y" :label="y + '年'" :value="y" />
        </el-select>
      </template>

      <template v-if="exportType === 'month'">
        <el-select v-model="year" placeholder="选择年份" size="small" style="width: 120px; margin-left: 10px;">
          <el-option v-for="y in years" :key="y" :label="y + '年'" :value="y" />
        </el-select>
        <el-select v-model="month" placeholder="选择月份" size="small" style="width: 100px; margin-left: 10px;">
          <el-option v-for="m in months" :key="m" :label="m + '月'" :value="m" />
        </el-select>
      </template>

      <template v-if="exportType === 'custom'">
        <el-date-picker v-model="startDate" type="date" placeholder="开始日期" size="small" style="width: 160px; margin-left: 10px;" />
        <el-date-picker v-model="endDate" type="date" placeholder="结束日期" size="small" style="width: 160px; margin-left: 10px;" />
      </template>

      <el-button type="default" size="small" style="margin-left: 10px;" @click="loadBills">查询</el-button>
      <el-button type="default" size="small" style="margin-left: 10px;" @click="handleReset">重置</el-button>
      <el-button type="primary" size="small" style="margin-left: 10px;" @click="handleExport">导出账单</el-button>
    </div>

    <el-table :data="billList" v-loading="loading" border stripe>
      <el-table-column prop="orderNo" label="订单号" min-width="160" />
      <el-table-column prop="goodsName" label="商品" min-width="120" />
      <el-table-column prop="tenantName" label="租客" min-width="100" />
      <el-table-column prop="rentDays" label="租期(天)" width="90" align="center" />
      <el-table-column label="租金" width="120" align="center">
        <template slot-scope="scope">
          ¥{{ formatPrice(scope.row.orderPrice) }}
        </template>
      </el-table-column>
      <el-table-column label="押金" width="120" align="center">
        <template slot-scope="scope">
          ¥{{ formatPrice(scope.row.depositPrice) }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)" size="small">
            {{ getStatusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rentStartTime" label="开始时间" width="160" />
      <el-table-column prop="rentEndTime" label="结束时间" width="160" />
      <el-table-column prop="createdAt" label="下单时间" width="160" />
      <el-table-column label="操作" width="100" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="viewDetail(scope.row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="billList.length === 0 && !loading" class="empty-state">
      <el-empty description="暂无账单数据" />
    </div>

    <el-pagination
      v-if="total > 0"
      :current-page="page"
      :page-size="pageSize"
      :total="total"
      layout="total, prev, pager, next"
      style="margin-top: 20px; text-align: right;"
      @current-change="handlePageChange"
    />
  </div>
</template>

<script>
import { getShopList } from '@/api/shop'
import { getOrderList, exportBill } from '@/api/order'
import { orderStatusMap, orderStatusTypeMap } from '@common/statusMap.js'

export default {
  name: 'RentBillList',
  data() {
    const now = new Date()
    const year = now.getFullYear()
    const years = []
    for (let i = year - 5; i <= year; i++) {
      years.push(i)
    }
    const months = []
    for (let i = 1; i <= 12; i++) {
      months.push(i)
    }

    return {
      shopList: [],
      shopId: '',
      billList: [],
      loading: false,
      page: 1,
      pageSize: 20,
      total: 0,
      exportType: 'month',
      year: year,
      month: now.getMonth() + 1,
      startDate: '',
      endDate: '',
      years,
      months
    }
  },
  mounted() {
    this.loadShops()
  },
  methods: {
    async loadShops() {
      try {
        const res = await getShopList({ page: 1, pageSize: 100 })
        this.shopList = res.data?.list || res.data?.records || []
        if (this.shopList.length > 0) {
          this.shopId = this.shopList[0].id
          this.loadBills()
        }
      } catch (e) {
        this.$message.error('加载店铺列表失败')
      }
    },
    handleShopChange() {
      this.page = 1
      this.loadBills()
    },
    async loadBills() {
      if (!this.shopId) return
      this.loading = true
      try {
        const params = {
          shopId: this.shopId,
          page: this.page,
          pageSize: this.pageSize,
          statusIn: 'RECEIVED,RENTING,RETURN_DELIVERED,RETURN_RECEIVED,FINISHED'
        }
        const res = await getOrderList(params)
        this.billList = res.data?.list || res.data?.records || []
        this.total = res.data?.total || 0
      } catch (e) {
        this.$message.error('加载账单失败')
      } finally {
        this.loading = false
      }
    },
    handlePageChange(page) {
      this.page = page
      this.loadBills()
    },
    viewDetail(row) {
      this.$router.push('/order/detail/' + row.id)
    },
    formatPrice(price) {
      if (!price) return '0.00'
      const num = Number(price)
      if (isNaN(num)) return '0.00'
      return num.toFixed(2)
    },
    handleReset() {
      const now = new Date()
      this.exportType = 'month'
      this.year = now.getFullYear()
      this.month = now.getMonth() + 1
      this.startDate = ''
      this.endDate = ''
      this.page = 1
      this.loadBills()
    },
    async handleExport() {
      if (!this.shopId) {
        this.$message.warning('请先选择店铺')
        return
      }

      let startDate = ''
      let endDate = ''

      if (this.exportType === 'year') {
        startDate = this.year + '-01-01'
        endDate = this.year + '-12-31'
      } else if (this.exportType === 'month') {
        const m = this.month.toString().padStart(2, '0')
        startDate = this.year + '-' + m + '-01'
        const daysInMonth = new Date(this.year, this.month, 0).getDate()
        endDate = this.year + '-' + m + '-' + daysInMonth
      } else if (this.exportType === 'custom') {
        if (!this.startDate || !this.endDate) {
          this.$message.warning('请选择开始日期和结束日期')
          return
        }
        startDate = this.startDate
        endDate = this.endDate
      }

      try {
        this.$message.info('正在导出，请稍候...')
        const res = await exportBill({
          shopId: this.shopId,
          startDate,
          endDate
        })
        
        if (res && res.url) {
          window.open(res.url, '_blank')
        } else {
          this.$message.error('导出失败')
        }
      } catch (e) {
        this.$message.error('导出失败: ' + (e.message || '未知错误'))
      }
    },
    getStatusType(status) {
      return orderStatusTypeMap[status] || 'info'
    },
    getStatusText(status) {
      return orderStatusMap[status] || status
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
}
.page-header {
  margin-bottom: 20px;
}
.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}
.filter-bar {
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #f8f9fa;
  border-radius: 8px;
}
.empty-state {
  padding: 60px 0;
}
</style>
